import React, { useState, useCallback, useEffect } from "react";
import { useDownloadExcel } from "react-export-table-to-excel";
import ReactPaginate from "react-paginate";

import { nanoid } from "nanoid";
import AddButton from "./addButton";
import EditButton from "./editButton";
import DeleteButton from "./deleteButton";
import { loadData } from "./hook";
import Calendar from "./Calendar";

import "../asset/css/dataTable.css";
import "react-date-range/dist/styles.css"; // main css file
import "react-date-range/dist/theme/default.css"; // theme css file

import { TFetcherParams, TPagination } from "../type";
import moment from "moment";
import { Col, Form, Row } from "react-bootstrap";

const tdStyle = {
	textAlign: "center" as "center",
	width: "100px"
};

export default function DataTable({
	name,
	data,
	mapFields,
	editPath,
	deletePath,
	fetcher,
	accessor,
	sumFields,
	actionAble = true,
	isGetDeleted = false,
	filters,
	converter = (d: any) => d
}: {
	name: string;
	data?: any[];
	mapFields: { [heading: string]: string };
	sumFields?: { [heading: string]: string };
	filters?: {
		[filterName: string]: {
			title: string;
			type: "date" | "select" | "text" | "number" | "date2";
			value: any;
			options?: { label: string; value: string }[];
			onChangeFilter?: (value: any) => void;
			onChangeDateFilter?: any;
			isFiltering?: boolean;
			onRemoveFilter?: () => void;
		};
	};
	editPath: string;
	deletePath: string;
	accessor?: string;
	actionAble?: boolean;
	isGetDeleted?: boolean;
	dateFilterLabel?: string;
	fetcher?: ({ page, limit, isGetDeleted }: TFetcherParams) => Promise<any>;
	converter?: Function;
}) {
	const [pagination, setPagination] = useState({
		page: 1,
		limit: 10,
		total: 0
	});

	const [tableData, setTableData] = useState(data || []);

	const callback = useCallback(
		(data: { [accessor: string]: any; pagination: TPagination }) => {
			setTableData(accessor ? data[accessor] : data);
			setPagination((prev) => ({
				...prev,
				total: data.pagination.totalItems
			}));
		},
		[accessor]
	);

	const enrichedFilters = React.useCallback(() => {
		if (!filters) return;

		const filter = Object.keys(filters).reduce(
			(
				acc: {
					[key: string]: any;
				},
				key
			) => {
				if (!filters[key].isFiltering) return acc;

				const filter = filters[key];
				if (filter.type === "date" || filter.type === "date2") {
					const { startDate, endDate } = filter.value;
					acc[key] = {
						startDate: +moment(startDate).format("X"),
						endDate: +moment(endDate).format("X")
					};
				}

				if (
					filter.type === "select" ||
					filter.type === "text" ||
					filter.type === "number"
				) {
					acc[key] = filter.value;
				}

				return acc;
			},
			{}
		);
		return filter;
	}, [filters]);

	React.useEffect(() => {
		loadData(
			fetcher &&
				fetcher({
					page: pagination.page,
					limit: pagination.limit,
					isGetDeleted,
					...enrichedFilters()
				}),
			callback
		);
	}, [
		callback,
		fetcher,
		pagination.limit,
		pagination.page,
		isGetDeleted,
		filters,
		enrichedFilters
	]);

	const headings = Object.keys(mapFields);
	const fields = Object.values(mapFields);
	const [isFocused, setIsFocused] = useState("");

	const handleInputChange = (e: any, onChange: any) => {
		onChange(e.target.value);
		setIsFocused(e.target.id);
	};

	const renderFilter = (data: any) => {
		switch (data.type) {
			case "date":
				return (
					<Calendar
						isEnabled={data.isFiltering}
						label={data.title}
						dateRangeStart={data.value.startDate}
						dateRangeEnd={data.value.endDate}
						onDateRangeChanged={(values) => {
							data.onChangeFilter(values);
						}}
						onRemoveFilter={data.onRemoveFilter}
					/>
				);

			case "date2":
				return (
					<Row>
						<Col sm="12">
							<Form.Control
								type="date"
								value={moment(data.value.startDate).format("YYYY-MM-DD")}
								onChange={(e) => {
									if (moment(e.target.value).isValid()) {
										data.onChangeDateFilter.start(e.target.value);
									}
								}}
							/>
						</Col>

						<Col sm="12">
							<Form.Control
								type="date"
								value={moment(data.value.endDate).format("YYYY-MM-DD")}
								onChange={(e) => {
									if (moment(e.target.value).isValid()) {
										data.onChangeDateFilter.end(e.target.value);
									}
								}}
							/>
						</Col>

						<Col sm="12">
							{data.isFiltering && (
								<button
									className={"btn btn-danger btn-sm mt-2"}
									onClick={() => data.onRemoveFilter()}
								>
									<i className="fas fa-trash mt-2" /> Xoá lọc
								</button>
							)}
						</Col>
					</Row>
				);

			case "select":
				return (
					<select
						value={data.value}
						className="custom-select custom-select-sm form-control form-control-sm"
						onChange={(e) => {
							data.onChangeFilter(e.target.value);
						}}
					>
						{data.options.map((v: any) => (
							<option key={nanoid()} value={v.value}>
								{v.label}
							</option>
						))}
					</select>
				);

			case "text":
				return (
					<input
						id={data.title}
						autoFocus={isFocused === data.title}
						type={data.type}
						className="form-control form-control-sm"
						value={data.value}
						onChange={(e) => {
							handleInputChange(e, data.onChangeFilter);
						}}
					/>
				);

			case "number":
				return (
					<input
						id={data.title}
						autoFocus={isFocused === data.title}
						type={data.type}
						className="form-control form-control-sm"
						value={data.value}
						onChange={(e) => {
							handleInputChange(e, data.onChangeFilter);
						}}
					/>
				);

			default:
				return null;
		}
	};

	const headingJSX = (
		<>
			<tr>
				<th
					style={{
						textAlign: "center"
					}}
				></th>
				{fields.map((f) => (
					<th
						key={nanoid()}
						style={{
							textAlign: "center"
						}}
					>
						{headings[fields.indexOf(f)]}
					</th>
				))}
			</tr>

			<tr>
				<th
					style={{
						textAlign: "center"
					}}
				></th>
				{fields.map((f) => (
					<th
						key={nanoid()}
						style={{
							textAlign: "center",
							verticalAlign: "middle"
						}}
					>
						<>{filters && filters[f] && renderFilter(filters[f])}</>
					</th>
				))}
			</tr>
		</>
	);

	const tbodyJSX = converter(tableData).map((d: any) => {
		return (
			<tr key={d.id}>
				{actionAble ? (
					<td style={tdStyle}>
						<EditButton href={`${editPath}/${d.id}`} />
						<DeleteButton url={`${deletePath}/${d.id}`} id={d.id} />
					</td>
				) : (
					<td></td>
				)}
				{fields.map((f) => (
					<td key={nanoid()}>{d[f]}</td>
				))}
			</tr>
		);
	});

	const tableRef = React.useRef(null);

	const { onDownload } = useDownloadExcel({
		currentTableRef: tableRef.current,
		filename: name,
		sheet: name
	});

	const convertSumFields = (sumFields: { [heading: string]: string }) => {
		const sumFieldsArray = Object.entries(sumFields);
		const sumFieldsJSX = sumFieldsArray.map(([heading, field]) => {
			const sum = tableData.reduce((acc: number, cur: any) => {
				return field !== "id" ? acc + cur[field] : acc + 1;
			}, 0);
			return (
				<div className="col-xl-3 col-md-6 mb-4" key={nanoid()}>
					<div className="card border-left-primary shadow h-100 py-2">
						<div className="card-body">
							<div className="row no-gutters align-items-center">
								<div className="col mr-2">
									<div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
										{heading}
									</div>
									<div className="h5 mb-0 font-weight-bold text-gray-800">
										{sum}
									</div>
								</div>
								<div className="col-auto">
									<i className="fas fa-clipboard-list fa-2x text-gray-300"></i>
								</div>
							</div>
						</div>
					</div>
				</div>
			);
		});
		return sumFieldsJSX;
	};

	return (
		<div className="card shadow mb-4">
			<div className="card-header py-3">
				<h6
					className="m-0 font-weight-bold text-primary"
					style={{ display: "inline-block" }}
				>
					{name}
				</h6>

				{actionAble && (
					<div style={{ position: "absolute", right: "20px", top: "14px" }}>
						<AddButton href={`${editPath}/new`} />
					</div>
				)}
			</div>
			<div className="card-body">
				<div className="row">
					<div className="col-6 col-md-2">
						<div className="d-flex align-items-center mb-4">
							<div className="mr-2">Show</div>
							<select
								className="custom-select custom-select-sm form-control form-control-sm"
								onChange={(e) => {
									const limit = parseInt(e.target.value);
									setPagination((prev) => ({
										...prev,
										limit
									}));
									loadData(fetcher && fetcher({ page: 1, limit }), callback);
								}}
							>
								<option value="10">10</option>
								<option value="25">25</option>
								<option value="50">50</option>
								<option value="100">100</option>
								<option value="500">500</option>
							</select>
							<div className="ml-2">Mục</div>
						</div>
					</div>

					<div className="position-relative">
						<button
							className="btn btn-primary btn-sm mx-1"
							onClick={onDownload}
							style={{ float: "right" }}
						>
							Xuất Excel
						</button>
					</div>
				</div>

				<div className="row">{sumFields && convertSumFields(sumFields)}</div>

				<div className="table-responsive">
					<table
						className="table table-striped table-bordered"
						id="dataTable"
						width="100%"
						cellSpacing="0"
						ref={tableRef}
					>
						<thead>{headingJSX}</thead>
						<tbody>{tbodyJSX}</tbody>
					</table>
				</div>

				<div className="d-flex justify-content-end mt-2">
					<ReactPaginate
						containerClassName="pagination"
						pageClassName="page-item"
						breakLabel=""
						nextLabel="next"
						pageLinkClassName="page-link"
						pageRangeDisplayed={3}
						pageCount={Math.ceil(pagination.total / pagination.limit)}
						previousLabel="previous"
						onPageChange={(selectedItem: { selected: number }) => {
							const page = selectedItem.selected + 1;
							setPagination((prev) => ({
								...prev,
								page
							}));
						}}
						renderOnZeroPageCount={null}
						previousClassName="paginate_button page-item previous"
						nextClassName="paginate_button page-item next"
						previousLinkClassName="page-link"
						nextLinkClassName="page-link"
						activeClassName="active"
					/>
				</div>
			</div>
		</div>
	);
}
